import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ConfigService } from '../../services/config.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslocoModule } from '@jsverse/transloco';
import { BrandLogoComponent } from '../../shared/brand-logo/brand-logo.component';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';
import { VdButtonComponent } from '../../ui/button/button.component';
import { LanguageSwitcherComponent } from '../../i18n/language-switcher.component';
import { SESSION_COOKIE_DAYS } from '../../shared/constants/session';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    BrandLogoComponent,
    ThemeToggleComponent,
    VdButtonComponent,
    LanguageSwitcherComponent,
  ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  errorMessage: string | null = null;
  showPassword: boolean = false;
  isLoading: boolean = false;
  emailFocused: boolean = false;
  passwordFocused: boolean = false;

  // Modal de choix d'accès : agent (vendeur, public) vs staff (vendeur.site, VPN).
  showAccessModal: boolean = false;


  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private injector: Injector,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  ngOnInit(): void {
    // Si l'utilisateur revient sur /login alors qu'il a déjà des cookies
    // d'auth (ex : flow SSO terminé via /sso/* → /auth/landing), on
    // auto-redirige vers le dashboard.
    try {
      if (this.cookieService.get('access_token') && this.cookieService.get('user_id')) {
        this.router.navigate(['/Accueil']);
      }
    } catch { /* noop */ }

    // Affichage des erreurs OAuth (Facebook/Google login). Le backend
    // redirige ici en cas d'échec : /login?oauth_error=…&detail=…
    const oauthErr = this.route.snapshot.queryParamMap.get('oauth_error');
    const oauthDetail = this.route.snapshot.queryParamMap.get('detail');
    if (oauthErr) {
      this.errorMessage = this.mapOAuthError(oauthErr, oauthDetail);
    }

    // Erreur SSO Authentik existante (rétrocompatibilité).
    const ssoErr = this.route.snapshot.queryParamMap.get('sso_error');
    if (ssoErr && !this.errorMessage) {
      this.errorMessage = `Erreur SSO : ${ssoErr}`;
    }
  }

  private mapOAuthError(code: string, detail: string | null): string {
    const map: Record<string, string> = {
      provider_not_configured:
        "Ce mode de connexion n'est pas encore configuré côté serveur.",
      unknown_provider: "Provider de connexion inconnu.",
      missing_code_or_state: "Le provider n'a pas renvoyé de code d'authentification.",
      invalid_state: "Le jeton de sécurité a expiré. Recommencez la connexion.",
      provider_mismatch: "Conflit de provider. Recommencez la connexion.",
      nonce_mismatch: "Conflit de session. Recommencez la connexion.",
      auth_failed: "Authentification refusée par le provider.",
      provider_no_email:
        "Le provider n'a pas fourni votre email — autorisez l'accès à votre email puis réessayez.",
      identity_conflict:
        "Ce compte est déjà lié à un autre utilisateur vendeur.site. Connectez-vous avec ce dernier puis déliez-le pour le réutiliser.",
      access_denied: "Vous avez refusé l'autorisation au provider.",
      unexpected: "Erreur inattendue. Réessayez ou contactez le support.",
    };
    const base = map[code] || `Échec OAuth (${code}).`;
    return detail ? `${base} (${detail})` : base;
  }

  ngOnDestroy(): void { /* no resource to clean up since we removed the polling */ }

  /**
   * Animation du logo au clic
   */
  triggerLogoAnimation(): void {
    // Animation déclenchée via CSS class
  }

  /**
   * Connexion avec CodinCloud SSO (Authentik) — flow SERVER-SIDE.
   *
   * Le frontend ne fait QUE redirige le navigateur vers le backend, qui :
   *  1. génère un ``state`` JWT signé (anti-CSRF), pose un cookie HttpOnly
   *     de double-check, puis redirige vers Authentik ;
   *  2. au retour ``/auth/callback`` (côté API), échange le code↔token
   *     côté serveur (le ``client_secret`` n'est jamais exposé au client) ;
   *  3. redirige vers ``vendeur.site/auth/landing#at=…&rt=…`` (fragment URL
   *     non envoyé au serveur → safe vs logs / CF / proxies).
   *
   * Mode PWA standalone : pour contourner le bug du captcha Cloudflare
   * Turnstile (stage captcha d'Authentik) qui ne se valide pas dans le
   * contexte standalone (cookies tiers ``challenges.cloudflare.com``
   * bloqués), on ouvre le SSO dans le navigateur natif via ``_blank``
   * et on poll les cookies d'auth pour reprendre la main automatiquement.
   * Sur Android Chrome, les cookies sont partagés entre la PWA et Chrome
   * (même profil Chromium) → l'app se débloque sans intervention.
   */
  /**
   * Lance le flow SSO server-side : on redirige sur le backend
   * ``/auth/sso/start`` qui :
   *  1. génère un ``state`` JWT signé (anti-CSRF) + cookie HttpOnly,
   *  2. redirige vers ``sso.codincloud.com/application/o/authorize/…`` (direct,
   *     pas de proxy, le flow standard est fiable),
   *  3. au retour de Authentik (``/auth/callback``), échange code↔token côté
   *     serveur (le ``client_secret`` n'est jamais exposé au client),
   *  4. redirige vers ``vendeur.site/auth/landing#at=…`` (PWA + Web).
   */
  loginWithCodinCloud(): void {
    const configService = this.injector.get(ConfigService);
    const apiUrl = configService.apiUrl?.replace(/\/+$/, '') || '';
    const apiRoot = apiUrl.replace(/\/authentications$/, '');
    const returnTo = encodeURIComponent('/Accueil');
    window.location.href = `${apiRoot}/auth/sso/start?return_to=${returnTo}`;
  }

  /**
   * Connexion avec Facebook (OAuth Login flow).
   *
   * Pointe vers ``vendeur.site/auth/oauth/facebook/start`` (same-origin,
   * proxyfié par Nginx vers vendor_api). On NE passe PAS par
   * vendor-api.codincloud.com directement pour éviter le Cloudflare
   * Managed Challenge (sinon le browser stoppe le redirect cross-origin
   * sans cookie cf_clearance → flow cassé).
   *
   * Au retour, l'API redirige vers vendeur.site/auth/landing#at=…
   * (même mécanique que le SSO Authentik), donc l'AuthLandingComponent
   * existant gère les tokens sans modification.
   */
  loginWithFacebook(): void {
    const returnTo = encodeURIComponent('/Accueil');
    window.location.href = `/auth/oauth/facebook/start?return_to=${returnTo}`;
  }

  /**
   * Connexion avec Google (OAuth Login flow).
   *
   * Activable dès que GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET sont
   * configurés côté serveur. Si non configuré, le backend redirige
   * vers /login?oauth_error=provider_not_configured.
   */
  loginWithGoogle(): void {
    const returnTo = encodeURIComponent('/Accueil');
    window.location.href = `/auth/oauth/google/start?return_to=${returnTo}`;
  }

  /**
   * Ouvre/ferme le modal de choix d'accès séparé.
   *
   * Distinction métier :
   *  - Agent  = personnel d'un VENDEUR (PosAgent). Accès PUBLIC via le terminal
   *    POS (code + PIN) ou le lien QR/email/WhatsApp de son créneau.
   *  - Staff  = personnel de la PLATEFORME vendeur.site. Accès VPN uniquement.
   */
  openAccessModal(): void {
    this.showAccessModal = true;
  }

  closeAccessModal(): void {
    this.showAccessModal = false;
  }

  /** Accès agent (vendeur) : terminal POS public (code + PIN). */
  goAgent(): void {
    this.showAccessModal = false;
    this.router.navigate(['/pos/terminal']);
  }

  /** Accès staff (vendeur.site) : page de connexion VPN-only. */
  goStaff(): void {
    this.showAccessModal = false;
    this.router.navigate(['/staff/login']);
  }

  /**
   * Soumission du formulaire de connexion
   */
  onLogin(): void {
    this.errorMessage = null;
    
    if (this.loginForm.invalid) {
      this.errorMessage = 'Veuillez remplir correctement tous les champs.';
      return;
    }

    this.isLoading = true;
    const loginData = this.loginForm.value;

    this.authService.loginuser(loginData).subscribe({
      next: (response) => {
        this.isLoading = false;
        
        // Stockage des tokens dans les cookies (aligné JWT access/refresh)
        const cookieOptions = {
          expires: SESSION_COOKIE_DAYS,
          path: '/',
          secure: true,
          sameSite: 'Strict' as const
        };

        if (response.access_token) {
          this.cookieService.set('access_token', response.access_token, cookieOptions);
        }
        if (response.refresh_token) {
          this.cookieService.set('refresh_token', response.refresh_token, cookieOptions);
        }
        if (response.user_id) {
          this.cookieService.set('user_id', response.user_id.toString(), cookieOptions);
        }

        // Redirection selon l'état du profil
        if (this.authService.getCompleteSignUpFromToken()) {
          this.router.navigate(['/Accueil']);
        } else {
          this.router.navigate(['/CompleteSignUp']);
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Erreur de connexion:', error);

        if (error.error?.detail === 'Please Verify Your Email') {
          this.errorMessage = 'Veuillez vérifier votre adresse email.';
        } else if (error.status === 401) {
          this.errorMessage = 'Email ou mot de passe incorrect.';
        } else if (error.status === 0) {
          this.errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
        } else {
          this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.';
        }
      }
    });
  }
}
